/* Shared page motion for the Pass or Don’t Pay Promise and About Us pages: headline
   typewriter, count-ups, word-by-word reveal, the pillar card hover glow and
   a generic scrolled-into-view flag. Both pages were designed from the same
   prototype and use the same primitives, so they share one runtime rather than
   carrying two copies of it under different names.

   Everything here is a no-op when the visitor prefers reduced motion — the
   final text is written immediately so the content is never withheld, only the
   animation is skipped. */
(function () {
  'use strict';

  /* Several sections on this page each request this file, and the browser runs
     it once per tag. Bail out after the first pass rather than stacking
     duplicate observers and document-level listeners. */
  if (window.promisePageMotionLoaded) return;
  window.promisePageMotionLoaded = true;

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Touch visitors have no cursor to track, so the card hover glow would only
     burn battery. */
  var coarsePointer = window.matchMedia('(hover: none)').matches;

  /* ---- Headline typewriter -------------------------------------------------
     The heading ships with its real text in the markup so it is present for
     crawlers and for no-JS visitors; we only clear and retype it once we know
     JS is running and motion is welcome. */
  function initTypewriter(root) {
    var el = (root || document).querySelector('[data-promise-typewriter]');
    if (!el || el.dataset.typewriterReady === 'true') return;
    el.dataset.typewriterReady = 'true';

    var lines = Array.prototype.map.call(el.querySelectorAll('[data-typewriter-line]'), function (node) {
      return { node: node, text: node.textContent };
    });
    if (!lines.length) return;

    if (prefersReduced) {
      el.classList.add('is-typed');
      return;
    }

    lines.forEach(function (line) { line.node.textContent = ''; });
    el.classList.add('is-typing');

    var delay = parseInt(el.getAttribute('data-initial-delay'), 10);
    if (isNaN(delay)) delay = 300;
    var speed = parseInt(el.getAttribute('data-speed'), 10);
    if (isNaN(speed)) speed = 34;

    var lineIndex = 0;
    var charIndex = 0;

    function step() {
      var line = lines[lineIndex];
      if (!line) {
        el.classList.remove('is-typing');
        el.classList.add('is-typed');
        return;
      }
      line.node.textContent = line.text.slice(0, charIndex + 1);
      charIndex += 1;
      if (charIndex >= line.text.length) {
        lineIndex += 1;
        charIndex = 0;
      }
      window.setTimeout(step, speed);
    }

    window.setTimeout(step, delay);
  }

  /* ---- Count-up ------------------------------------------------------------
     Runs when the figure scrolls into view. The element keeps its final value
     in the markup; we stash it, count from zero, then restore it exactly so a
     rounding error can never leave the wrong number on screen. */
  function initCounters(root) {
    var nodes = (root || document).querySelectorAll('[data-promise-count]');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.countReady === 'true') return;
      node.dataset.countReady = 'true';

      var finalText = node.textContent.trim();
      var match = finalText.match(/([^\d]*)([\d,]+)(.*)/);
      if (!match || prefersReduced) return;

      var prefix = match[1];
      var suffix = match[3];
      var target = parseInt(match[2].replace(/,/g, ''), 10);
      if (isNaN(target)) return;

      var duration = parseInt(node.getAttribute('data-duration'), 10);
      if (isNaN(duration)) duration = 1100;

      var run = function () {
        var start = null;
        function frame(now) {
          if (start === null) start = now;
          var progress = Math.min((now - start) / duration, 1);
          // easeOutCubic — fast start, gentle settle
          var eased = 1 - Math.pow(1 - progress, 3);
          if (progress < 1) {
            node.textContent = prefix + Math.round(target * eased).toLocaleString() + suffix;
            window.requestAnimationFrame(frame);
          } else {
            node.textContent = finalText;
          }
        }
        window.requestAnimationFrame(frame);
      };

      if (!('IntersectionObserver' in window)) { run(); return; }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          node.textContent = prefix + '0' + suffix;
          run();
        });
      }, { threshold: 0.4 });

      observer.observe(node);
    });
  }

  /* ---- Word-by-word lead reveal --------------------------------------------
     The paragraph ships as ordinary prose. We only split it into per-word spans
     once we know motion is welcome, because the CSS starts those spans at
     opacity 0 — a visitor without JS, or with reduced motion, must never end up
     looking at an invisible paragraph. */
  function initWordReveal(root) {
    var nodes = (root || document).querySelectorAll('[data-promise-word-reveal]');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.wordRevealReady === 'true') return;
      node.dataset.wordRevealReady = 'true';
      if (prefersReduced) return;

      var words = (node.textContent || '').trim().split(/\s+/);
      if (words.length < 2) return;

      var step = parseInt(node.getAttribute('data-word-delay'), 10);
      if (isNaN(step)) step = 45;

      node.textContent = '';
      words.forEach(function (word, index) {
        var span = document.createElement('span');
        span.className = 'promise-wr-word';
        span.textContent = word;
        span.style.transitionDelay = index * step + 'ms';
        node.appendChild(span);
        // A real space between the spans, so the text still wraps naturally
        // and copies out of the page as normal prose.
        node.appendChild(document.createTextNode(' '));
      });

      var reveal = function () { node.classList.add('is-word-revealed'); };

      if (!('IntersectionObserver' in window)) { reveal(); return; }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          reveal();
        });
      }, { threshold: 0.3 });

      observer.observe(node);
    });
  }

  /* ---- Scrolled-into-view flag ---------------------------------------------
     Adds .is-in-view once, for CSS that staggers its own children (the About
     page's founder quote). Reveals immediately when motion is unwelcome or
     IntersectionObserver is missing, so the content can never stay hidden. */
  function initInView(root) {
    var nodes = (root || document).querySelectorAll('[data-promise-inview]');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.inviewReady === 'true') return;
      node.dataset.inviewReady = 'true';

      var reveal = function () { node.classList.add('is-in-view'); };

      if (prefersReduced || !('IntersectionObserver' in window)) { reveal(); return; }

      /* Low threshold on purpose: this flag gates whether text is visible at
         all, and a card taller than the viewport can never reach a high
         ratio. Firing early is harmless; never firing is not. */
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          reveal();
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

      observer.observe(node);
    });
  }

  /* ---- Spark burst ---------------------------------------------------------
     Throws a ring of short-lived dots out of an element's icon when the pointer
     arrives — the Giving Back page's partnership heart. Purely decorative, so
     it is skipped entirely for reduced motion and touch. */
  function initSparkBurst(root) {
    var hosts = (root || document).querySelectorAll('[data-spark-burst]');
    if (!hosts.length) return;

    Array.prototype.forEach.call(hosts, function (host) {
      if (host.dataset.sparkReady === 'true') return;
      host.dataset.sparkReady = 'true';
      if (prefersReduced || coarsePointer) return;

      var last = 0;

      host.addEventListener('mouseenter', function () {
        // The burst reads as one gesture; re-firing on every re-entry turns it
        // into confetti, so ignore repeats inside the animation's lifetime.
        var now = Date.now();
        if (now - last < 700) return;
        last = now;

        var anchor = host.querySelector('svg');
        if (!anchor) return;

        var hostRect = host.getBoundingClientRect();
        var iconRect = anchor.getBoundingClientRect();
        var originX = iconRect.left - hostRect.left + iconRect.width / 2;
        var originY = iconRect.top - hostRect.top + iconRect.height / 2;

        for (var i = 0; i < 12; i++) {
          var spark = document.createElement('span');
          spark.className = 'giving-back__spark';

          var angle = (Math.PI * 2 * i) / 12 + (Math.random() - 0.5) * 0.5;
          var distance = 22 + Math.random() * 30;
          var scale = 0.5 + Math.random() * 0.8;
          var life = 600 + Math.random() * 400;

          spark.style.setProperty('--x', originX + 'px');
          spark.style.setProperty('--y', originY + 'px');
          spark.style.setProperty('--dx', Math.cos(angle) * distance + 'px');
          spark.style.setProperty('--dy', Math.sin(angle) * distance + 'px');
          spark.style.setProperty('--dur', life + 'ms');
          spark.style.width = 6 * scale + 'px';
          spark.style.height = 6 * scale + 'px';

          host.appendChild(spark);
          window.setTimeout(
            (function (node) {
              return function () { node.remove(); };
            })(spark),
            life + 200
          );
        }
      });
    });
  }

  /* ---- Pillar cards --------------------------------------------------------
     Each card lights the edge nearest the pointer, drifts a few particles while
     hovered, and rings out on click. Deliberately no tilt — the pillars keep
     the flat lift the rest of the theme uses. There is no background glow
     following the cursor: the page background stays still, as on the home
     page. */
  var PROXIMITY = 60; // px from a card edge where the glow is at full strength
  var FADE_EDGE = 80; // px where it has faded out entirely

  function glowStrength(distance) {
    if (distance <= PROXIMITY) return 1;
    if (distance >= FADE_EDGE) return 0;
    return (FADE_EDGE - distance) / (FADE_EDGE - PROXIMITY);
  }

  function initCardParticles(card) {
    // One burst of six per pointer entry, cleared early if the pointer leaves
    // before they finish drifting.
    var live = [];

    card.addEventListener('mouseenter', function () {
      for (var i = 0; i < 6; i++) {
        var dot = document.createElement('span');
        dot.className = 'promise-bento-particle';
        dot.style.left = Math.random() * card.clientWidth + 'px';
        dot.style.top = Math.random() * card.clientHeight + 'px';
        card.appendChild(dot);
        live.push(dot);

        var life = 1800 + Math.random() * 800;
        dot.animate(
          [
            { transform: 'translate(0, 0)', opacity: 1 },
            {
              transform: 'translate(' + ((Math.random() - 0.5) * 30).toFixed(1) + 'px, ' +
                ((Math.random() - 0.5) * 30).toFixed(1) + 'px)',
              opacity: 0
            }
          ],
          { duration: life, easing: 'ease-out', fill: 'forwards' }
        );
        window.setTimeout(
          (function (particle) {
            return function () { particle.remove(); };
          })(dot),
          life
        );
      }
    });

    card.addEventListener('mouseleave', function () {
      live.splice(0).forEach(function (dot) { dot.remove(); });
      card.style.setProperty('--glow-intensity', '0');
    });

    card.addEventListener('click', function (event) {
      var rect = card.getBoundingClientRect();
      var x = event.clientX - rect.left;
      var y = event.clientY - rect.top;
      // Reach the furthest corner so the ring always clears the whole card.
      var reach = Math.max(
        Math.sqrt(x * x + y * y),
        Math.sqrt((rect.width - x) * (rect.width - x) + y * y),
        Math.sqrt(x * x + (rect.height - y) * (rect.height - y)),
        Math.sqrt((rect.width - x) * (rect.width - x) + (rect.height - y) * (rect.height - y))
      );

      var ripple = document.createElement('span');
      ripple.className = 'promise-bento-ripple';
      ripple.style.width = reach * 2 + 'px';
      ripple.style.height = reach * 2 + 'px';
      ripple.style.left = x - reach + 'px';
      ripple.style.top = y - reach + 'px';
      card.appendChild(ripple);

      ripple.animate(
        [{ transform: 'scale(0)', opacity: 1 }, { transform: 'scale(1)', opacity: 0 }],
        { duration: 700, easing: 'ease-out', fill: 'forwards' }
      );
      window.setTimeout(function () { ripple.remove(); }, 700);
    });
  }

  function initBento(root) {
    if (prefersReduced || coarsePointer) return;

    var grids = (root || document).querySelectorAll('.promise-bento');
    if (!grids.length) return;

    Array.prototype.forEach.call(grids, function (grid) {
      if (grid.dataset.bentoReady === 'true') return;

      var cards = grid.querySelectorAll('.promise-bento-card');
      if (!cards.length) return;
      grid.dataset.bentoReady = 'true';

      Array.prototype.forEach.call(cards, initCardParticles);

      var pending = null;

      var paint = function (clientX, clientY) {
        pending = null;
        var gridRect = grid.getBoundingClientRect();
        var inside =
          clientX >= gridRect.left && clientX <= gridRect.right &&
          clientY >= gridRect.top && clientY <= gridRect.bottom;

        if (!inside) {
          Array.prototype.forEach.call(cards, function (card) {
            card.style.setProperty('--glow-intensity', '0');
          });
          return;
        }

        Array.prototype.forEach.call(cards, function (card) {
          var rect = card.getBoundingClientRect();
          var dx = clientX - (rect.left + rect.width / 2);
          var dy = clientY - (rect.top + rect.height / 2);
          // Distance to the card's bounding circle, not its centre, so a wide
          // card is not treated as further away than a square one.
          var distance = Math.max(
            0,
            Math.sqrt(dx * dx + dy * dy) - Math.max(rect.width, rect.height) / 2
          );

          card.style.setProperty('--glow-x', (clientX - rect.left) / rect.width * 100 + '%');
          card.style.setProperty('--glow-y', (clientY - rect.top) / rect.height * 100 + '%');
          card.style.setProperty('--glow-intensity', glowStrength(distance).toFixed(3));
        });
      };

      // Coalesced to one paint per frame: this listener is document-wide, so it
      // fires for pointer movement anywhere on the page.
      var onMove = function (event) {
        // The theme editor swaps the whole section on every settings change.
        // The replaced grid is gone for good, so its handler retires on sight
        // instead of measuring a detached grid on every move.
        if (!grid.isConnected) {
          document.removeEventListener('mousemove', onMove);
          return;
        }
        if (pending !== null) window.cancelAnimationFrame(pending);
        var x = event.clientX;
        var y = event.clientY;
        pending = window.requestAnimationFrame(function () { paint(x, y); });
      };

      document.addEventListener('mousemove', onMove);
    });
  }

  function init(root) {
    initTypewriter(root);
    initCounters(root);
    initWordReveal(root);
    initInView(root);
    initSparkBurst(root);
    initBento(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders a section without a full page load.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
